import { useEffect, useMemo, useState } from "react";
import { Field, Fieldset } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import FormInput from "../../Form/FormInput";
import FormSelect from "../../Form/FormSelect";
import FormError from "../../Form/FormError";
import Button from "../../Button";
import SlideOver from "../../Modal/SlideOver";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";
import {
  addLocalNotification,
  clearAccountingAccount,
  clearAcctAccountError,
  requestCreateAccountingAccount,
  requestUpdateAccount,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useNotificationDispatch,
} from "../../../../Context";
import { ACCOUNT_CATEGORY_OPTIONS, ACCOUNT_CODE } from "../../../../Constants";
import FormRadioGroup from "../../Form/FormRadioGroup";
import {
  CURRENT_LIABILITY_CATEGORY,
  LONG_TERM_LIABILITY_CATEGORY,
  canChangeLiabilityTerm,
} from "../../../../Utils/balanceSheet";

// Module-level so the selected option keeps its identity across renders;
// the radio group matches the value by reference.
const TERM_OPTIONS = [
  {
    name: CURRENT_LIABILITY_CATEGORY,
    label: "accounts.fields.term.current",
  },
  {
    name: LONG_TERM_LIABILITY_CATEGORY,
    label: "accounts.fields.term.longTerm",
  },
];

const ChartOfAccountModal = ({ open, onClose, account, initialValues }) => {
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [category, setCategory] = useState(null);
  const [code, setCode] = useState("");
  const [initialCode, setInitialCode] = useState("110");
  const [errors, setErrors] = useState({});

  const dispatch = useAccountingDispatch();
  const notificationDispatch = useNotificationDispatch();

  const { user, getUserLoading: userLoading } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);
  const {
    submitLoading: accountLoading,
    submitError: accountError,
    inputError,
    accountID,
  } = useAccountingState();

  const isEdit = Boolean(account);
  const showTerm = isEdit && canChangeLiabilityTerm(account);
  const categoryChanged =
    showTerm && category?.value && category.value !== account.category;

  const submitLoading = userLoading || accountLoading;

  const accountCategories = useMemo(() => {
    return ACCOUNT_CATEGORY_OPTIONS.map((item) => ({
      ...item,
      label: t(item.label),
    }));
  }, [t]);

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      clearAccountingAccount(dispatch);
      clearAcctAccountError(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset / populate form whenever modal opens
  useEffect(() => {
    if (!open) return;

    // Reset first
    setName("");
    setCategory(null);
    setCode("");
    setInitialCode("110");
    setErrors({});

    if (account) {
      // Edit mode – populate from account
      setName(account.name);
      setCategory(
        accountCategories.find((c) => c.value === account.category) ?? null,
      );
      setInitialCode(account.code.toString().slice(0, 3));
      setCode(account.code.toString().slice(3));
    } else if (initialValues) {
      // Create mode with pre-filled values (redirected from another page)
      if (initialValues.name) setName(initialValues.name);
      if (initialValues.category) {
        const found = accountCategories.find(
          (c) => c.value === initialValues.category,
        );
        if (found) {
          setCategory(found);
          const codePrefix = ACCOUNT_CODE[found.value];
          if (codePrefix) setInitialCode(codePrefix);
        }
      }
      if (initialValues.code && initialValues.code.length > 3) {
        setCode(initialValues.code.toString().slice(3));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (accountID) {
      // Close modal
      onClose();
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `account-${accountID}`,
        type: "success",
        title: "common.success",
        body: isEdit
          ? "accounts.notifications.updateSuccess"
          : "accounts.notifications.createSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, accountID, isEdit, onClose]);

  const clearError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const handleFieldChange = (setter, field) => (eventOrValue) => {
    const value =
      eventOrValue?.target !== undefined
        ? eventOrValue.target.value
        : eventOrValue;
    setter(value);
    clearError(field);
  };

  const handleCategoryChange = (selectedOption) => {
    setCategory(selectedOption);
    setInitialCode(ACCOUNT_CODE[selectedOption?.value]);
    clearError("category");
  };

  // Moving a liability between current and long-term only swaps its category;
  // the read-only Kategori field follows the choice before saving.
  const handleTermChange = (option) => {
    setCategory(
      accountCategories.find((c) => c.value === option.name) ?? null,
    );
    clearAcctAccountError(dispatch);
  };

  const validate = () => {
    const newErrors = {};

    if (!name) {
      newErrors.name = "accounts.errors.nameRequired";
    }

    if (!category?.value) {
      newErrors.category = "accounts.errors.categoryRequired";
    }

    if (!code) {
      newErrors.code = "accounts.errors.codeRequired";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    saveAccount();
  };

  const saveAccount = () => {
    const input = isEdit
      ? {
          account_id: account.id,
          name,
          ...(categoryChanged && { category: category.value }),
        }
      : {
          business_id: user.mainBusiness.id,
          name,
          code: initialCode + code,
          category: category?.value,
        };

    const request = isEdit
      ? requestUpdateAccount
      : requestCreateAccountingAccount;
    request(dispatch, { input });
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={isEdit ? "accounts.modal.title.edit" : "accounts.modal.title.add"}
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={writeBlocked || submitLoading}
            loading={submitLoading}
            type="submit"
            form="new-account-form"
            variant="primary"
          >
            {isEdit ? t("common.save") : t("common.create")}
          </Button>
        </div>
      }
    >
      <form id="new-account-form" className="space-y-4" onSubmit={handleSubmit}>
        <WriteBlockGate>
        {/* A category move that the server rejects reports under the
            Jatuh tempo field instead. */}
        {!categoryChanged && <FormError error={accountError} />}
        <Fieldset
          disabled={submitLoading}
          className="space-y-4 data-[disabled]:opacity-50"
        >
          <Field>
            <FormInput
              required
              label="accounts.fields.name"
              id="name"
              name="name"
              value={name}
              onChange={handleFieldChange(setName, "name")}
              onClear={() => setName("")}
              placeholder={t("accounts.placeholders.name")}
              error={errors.name || inputError?.error?.name?.[0]}
            />
          </Field>

          <Field>
            <FormSelect
              label="accounts.fields.category"
              id="category"
              name="category"
              disabled={isEdit}
              searchable
              required
              options={accountCategories}
              value={category}
              onChange={handleCategoryChange}
              placeholder={t("accounts.placeholders.category")}
              error={errors.category || inputError?.error?.category?.[0]}
            />
          </Field>

          {showTerm && (
            <div>
              <FormRadioGroup
                label="accounts.fields.term.label"
                name="term"
                options={TERM_OPTIONS}
                value={TERM_OPTIONS.find((o) => o.name === category?.value)}
                onChange={handleTermChange}
                error={categoryChanged ? accountError : undefined}
              />
              <p className="mt-2 text-sm text-gray-500">
                {t("accounts.fields.term.hint")}
              </p>
            </div>
          )}

          <Field>
            <FormInput
              required
              label="accounts.fields.code"
              id="code"
              name="code"
              value={code}
              disabled={isEdit}
              onChange={handleFieldChange(setCode, "code")}
              onClear={() => setCode("")}
              placeholder={t("accounts.placeholders.code")}
              numberFormat={{
                thousandSeparator: true,
              }}
              prefix={initialCode}
              maxLength={3}
              error={errors.code || inputError?.error?.code?.[0]}
              allowLeadingZeros
            />
          </Field>
        </Fieldset>
        </WriteBlockGate>
      </form>
    </SlideOver>
  );
};

export default ChartOfAccountModal;
